/**
 * Générateur — Voir dans l'espace des assemblages de cubes
 * Attendu ID : 674 | Niveau : 6ème | Domaine : Espace et géométrie (cluster 38)
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - decompte_pave_saisie              : saisie (nombre de cubes d'un pavé droit)
 *   - decompte_cube_saisie              : saisie (n³ ou complément à m³)
 *   - vocabulaire_cube_qcm              : QCM 4 choix (faces / arêtes / sommets / nature)
 *   - proprietes_cube_vf                : V/F 3V + 3F (propriétés cube + empilements)
 *   - escalier_empilement_qcm           : QCM 4 choix (escalier / retrait / couche inf)
 *
 * Spécificité : 100 % texte pur (pas de SVG — cohérence ligne éditoriale clusters
 * 31/34/36/37). Pas de point nommé à construire : les lettres servent uniquement
 * à enrichir les fingerprints du Format 3 via un identifiant de cube `ABCDEFGH`.
 *
 * Partition avec attendus voisins :
 *   - 647 : volume du cube (V = a³ en cm³). 674 compte des PETITS CUBES (entier sans unité).
 *   - 644-646 : aires. Pas de chevauchement.
 *   - 672/673 : symétrie axiale plane. Pas de chevauchement.
 *
 * Variantes estimées : plusieurs centaines à plusieurs milliers de fingerprints
 * par format (objectif : 20/20 uniques sur 20 itérations auditGen, stable sur
 * 5 runs consécutifs).
 */

import {
    randomChoice, shuffle, pickLettresSains, randInt,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 674;
// Pool de 22 lettres : I exclu (confusion 1) + O exclu (cohérence cluster 36 :
// O serait habituellement réservé à un centre, non utilisé ici mais on maintient
// la convention pour éviter toute confusion entre les clusters géométrie).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('').filter(c => c !== 'O');

// Pools numériques dédiés (cf. PAYLOADS_674.md)
const DIMS_POOL = [2, 3, 4, 5, 6];                       // 5 valeurs (pavés)
const ARETES_POOL = [2, 3, 4, 5, 6, 7, 8, 9, 10];        // 9 valeurs (cubes n³)
const ARETES_CIBLE_POOL = [3, 4, 5, 6];                  // 4 valeurs (compléments)
const ESCALIER_POOL = [3, 4, 5, 6, 7, 8];                // 6 valeurs (nb marches)
const LARGEUR_POOL = [1, 2, 3, 4, 5];                    // 5 valeurs (profondeur)

const L_PAVE     = 'Dénombrement d\'un pavé droit (saisie)';
const L_CUBE     = 'Dénombrement d\'un grand cube ou complément (saisie)';
const L_VOCAB    = 'Vocabulaire du cube (QCM)';
const L_VF       = 'Propriétés du cube et des assemblages (V/F)';
const L_EMP      = 'Escaliers et empilements (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_PAVE,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// ─── Moteur SVG isométrique (dupliqué depuis gen_648) ────────

const S = 18;       // demi-largeur d'une face de cube (plus petit que gen_648 pour tenir en preview)
const SH = S * 0.6;

const isoPos = (c, r, l) => ({
    x: (c - r) * S,
    y: (c + r) * SH / 2 - l * SH,
});

const drawCube = (c, r, l) => {
    const { x, y } = isoPos(c, r, l);
    const top = `${x},${y - SH} ${x + S},${y - SH / 2} ${x},${y} ${x - S},${y - SH / 2}`;
    const left = `${x - S},${y - SH / 2} ${x},${y} ${x},${y + SH} ${x - S},${y + SH / 2}`;
    const right = `${x},${y} ${x + S},${y - SH / 2} ${x + S},${y + SH / 2} ${x},${y + SH}`;
    return `<polygon points="${top}" fill="#dbeafe" stroke="#1e293b" stroke-width="1"/>`
        + `<polygon points="${left}" fill="#93c5fd" stroke="#1e293b" stroke-width="1"/>`
        + `<polygon points="${right}" fill="#60a5fa" stroke="#1e293b" stroke-width="1"/>`;
};

const buildAssemblageSVG = (cubes) => {
    // Painter's algorithm : arrière → avant pour que les cubes proches masquent les lointains.
    const sorted = [...cubes].sort((a, b) => {
        if (a.l !== b.l) return a.l - b.l;
        if ((a.c + a.r) !== (b.c + b.r)) return (a.c + a.r) - (b.c + b.r);
        return a.r - b.r;
    });

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const { c, r, l } of cubes) {
        const { x, y } = isoPos(c, r, l);
        minX = Math.min(minX, x - S);
        maxX = Math.max(maxX, x + S);
        minY = Math.min(minY, y - SH);
        maxY = Math.max(maxY, y + SH);
    }

    const pad = 12;
    const w = Math.ceil(maxX - minX + 2 * pad);
    const h = Math.ceil(maxY - minY + 2 * pad);
    const ox = -minX + pad;
    const oy = -minY + pad;

    const sig = sorted.map(({ c, r, l }) => `${c}${r}${l}`).join('').slice(0, 30);

    let svg = `<svg id="a${sig}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">`;
    svg += `<g transform="translate(${Math.round(ox)},${Math.round(oy)})">`;
    for (const { c, r, l } of sorted) svg += drawCube(c, r, l);
    svg += '</g></svg>';
    return svg;
};

const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// Variante de buildAssemblageSVG qui ajoute un trièdre de référence (flèches
// avant / droite / haut) dans le coin inférieur-gauche. Utilisé uniquement pour
// le format vue_solide_qcm où l'orientation doit être explicite.
const buildAssemblageSVGAvecTriedre = (cubes) => {
    const sorted = [...cubes].sort((a, b) => {
        if (a.l !== b.l) return a.l - b.l;
        if ((a.c + a.r) !== (b.c + b.r)) return (a.c + a.r) - (b.c + b.r);
        return a.r - b.r;
    });

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const { c, r, l } of cubes) {
        const { x, y } = isoPos(c, r, l);
        minX = Math.min(minX, x - S);
        maxX = Math.max(maxX, x + S);
        minY = Math.min(minY, y - SH);
        maxY = Math.max(maxY, y + SH);
    }

    const pad = 12;
    const triedreW = 75; // espace réservé à gauche pour le trièdre
    const triedreH = 60;
    const w = Math.ceil(maxX - minX + 2 * pad + triedreW);
    const h = Math.ceil(maxY - minY + 2 * pad + triedreH);
    const ox = -minX + pad + triedreW;
    const oy = -minY + pad;

    const sig = sorted.map(({ c, r, l }) => `${c}${r}${l}`).join('').slice(0, 30);

    // Trièdre : flèches dans les 3 directions (+c = droite = x ; +r = profondeur = y ; +l = haut = z)
    // +c visuel = bas-droite ; +r visuel = bas-gauche ; +l visuel = haut
    const TX = 28, TY = h - 18; // origine du trièdre
    const LEN = 22;
    // flèche +c (droite = axe x) → bas-droite
    const cx = TX + LEN * Math.sqrt(3) / 2, cy = TY + LEN * 0.5;
    // flèche +r (profondeur = axe y) → bas-gauche ; mais on veut "avant" (= -r)
    // Donc la flèche "avant" pointe dans la direction -r visuelle = haut-droite
    const ax = TX - LEN * Math.sqrt(3) / 2, ay = TY - LEN * 0.5;
    // flèche +l (haut = axe z) → verticale haut
    const hx = TX, hy = TY - LEN;

    const arrow = (x1, y1, x2, y2, color, label) => {
        const dx = x2 - x1, dy = y2 - y1;
        const L = Math.hypot(dx, dy) || 1;
        const ux = dx / L, uy = dy / L;
        const nx = -uy, ny = ux;
        const tip = 5;
        const t1x = x2 - tip * ux + tip * 0.5 * nx;
        const t1y = y2 - tip * uy + tip * 0.5 * ny;
        const t2x = x2 - tip * ux - tip * 0.5 * nx;
        const t2y = y2 - tip * uy - tip * 0.5 * ny;
        const lx = x2 + ux * 6 - (nx < 0 ? 2 : -2);
        const ly = y2 + uy * 6 + 4;
        return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${color}" stroke-width="1.4"/>`
            + `<polygon points="${x2.toFixed(1)},${y2.toFixed(1)} ${t1x.toFixed(1)},${t1y.toFixed(1)} ${t2x.toFixed(1)},${t2y.toFixed(1)}" fill="${color}"/>`
            + `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="10" fill="${color}" font-weight="600">${label}</text>`;
    };

    const triedre =
        // Origine (point noir)
        `<circle cx="${TX}" cy="${TY}" r="2" fill="#1e293b"/>`
        // "avant" : direction -r = haut-droite dans l'iso, mais on met plutôt "bas-droite" pour que visuellement ça corresponde à l'avant du solide. Ici on pointe vers +c car c'est la "largeur droite"
        // Non : +c = bas-droite = DROITE du solide. Pour "avant", il faut -r = haut-droite
        + arrow(TX, TY, ax, ay, '#7c3aed', 'avant')
        + arrow(TX, TY, cx, cy, '#dc2626', 'droite')
        + arrow(TX, TY, hx, hy, '#059669', 'haut');

    let svg = `<svg id="at${sig}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">`;
    svg += triedre;
    svg += `<g transform="translate(${Math.round(ox)},${Math.round(oy)})">`;
    for (const { c, r, l } of sorted) svg += drawCube(c, r, l);
    svg += '</g></svg>';
    return svg;
};

// Construit les cubes d'un pavé plein de dimensions a (longueur) × b (largeur) × c (hauteur).
const cubesPave = (a, b, c) => {
    const cubes = [];
    for (let i = 0; i < a; i++) for (let j = 0; j < b; j++) for (let k = 0; k < c; k++) {
        cubes.push({ c: i, r: j, l: k });
    }
    return cubes;
};

// Construit un escalier : n marches de profondeur 1, largeur p, hauteur = numéro de marche.
const cubesEscalier = (n, p) => {
    const cubes = [];
    for (let step = 0; step < n; step++) {
        for (let h = 0; h <= step; h++) {
            for (let w = 0; w < p; w++) {
                cubes.push({ c: step, r: w, l: h });
            }
        }
    }
    return cubes;
};

// Construit les `keep` premiers cubes d'un pavé a×b×c en remplissant couche par couche.
const cubesPartiels = (a, b, c, keep) => {
    const cubes = [];
    let count = 0;
    for (let l = 0; l < c && count < keep; l++) {
        for (let j = 0; j < b && count < keep; j++) {
            for (let i = 0; i < a && count < keep; i++) {
                cubes.push({ c: i, r: j, l });
                count++;
            }
        }
    }
    return cubes;
};

// Projette une liste de cubes sur une vue orthogonale (grille 2D 0/1).
// Convention iso scolaire (cohérente avec isoPos) :
//   - `c` = largeur (axe x, gauche→droite), +c va visuellement en bas-droite
//   - `r` = profondeur (axe y, avant→arrière), +r va visuellement en bas-gauche
//   - `l` = hauteur (axe z, bas→haut), +l va visuellement en haut
// Vues :
//   - 'face'   : observateur devant (r=-∞), regarde +r → projette sur plan (c, l)
//   - 'dessus' : observateur au-dessus (l=+∞), regarde -l → projette sur plan (c, r)
//   - 'gauche' : observateur à gauche (c=-∞), regarde +c → projette sur plan (r, l)
//   - 'droite' : observateur à droite (c=+∞), regarde -c → projette sur plan (r, l) miroir
const viewFromCubes = (cubes, axis) => {
    if (!cubes.length) return [[0]];
    const minC = Math.min(...cubes.map(p => p.c)), maxC = Math.max(...cubes.map(p => p.c));
    const minR = Math.min(...cubes.map(p => p.r)), maxR = Math.max(...cubes.map(p => p.r));
    const minL = Math.min(...cubes.map(p => p.l)), maxL = Math.max(...cubes.map(p => p.l));
    const largC = maxC - minC + 1;
    const profR = maxR - minR + 1;
    const hautL = maxL - minL + 1;

    if (axis === 'face') {
        // Lignes = hauteur (l=maxL en haut), colonnes = largeur (c=minC à gauche)
        const grid = Array.from({ length: hautL }, () => Array(largC).fill(0));
        for (const { c, l } of cubes) grid[maxL - l][c - minC] = 1;
        return grid;
    }
    if (axis === 'dessus') {
        // Lignes = profondeur (r=minR en haut = devant), colonnes = largeur
        const grid = Array.from({ length: profR }, () => Array(largC).fill(0));
        for (const { c, r } of cubes) grid[r - minR][c - minC] = 1;
        return grid;
    }
    if (axis === 'gauche') {
        // Lignes = hauteur, colonnes = profondeur (r=minR à gauche = devant)
        const grid = Array.from({ length: hautL }, () => Array(profR).fill(0));
        for (const { r, l } of cubes) grid[maxL - l][r - minR] = 1;
        return grid;
    }
    if (axis === 'droite') {
        // Miroir profondeur de la vue de gauche
        const grid = Array.from({ length: hautL }, () => Array(profR).fill(0));
        for (const { r, l } of cubes) grid[maxL - l][maxR - r] = 1;
        return grid;
    }
    return [[0]];
};

// Compare deux grilles 2D (égalité structurelle).
const gridEqual = (g1, g2) => {
    if (g1.length !== g2.length) return false;
    for (let i = 0; i < g1.length; i++) {
        if (g1[i].length !== g2[i].length) return false;
        for (let j = 0; j < g1[i].length; j++) if (g1[i][j] !== g2[i][j]) return false;
    }
    return true;
};

// Dessine une grille 2D en SVG (carrés colorés, fond transparent).
const svgVueGrille = (grid, color, salt) => {
    const cell = 22;
    const pad = 6;
    const rows = grid.length;
    const cols = Math.max(...grid.map(r => r.length));
    const w = cols * cell + 2 * pad;
    const h = rows * cell + 2 * pad;

    let body = '';
    for (let i = 0; i < rows; i++) {
        for (let j = 0; j < grid[i].length; j++) {
            if (grid[i][j]) {
                const x = pad + j * cell;
                const y = pad + i * cell;
                body += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="${color}" stroke="#1e293b" stroke-width="1.2"/>`;
            }
        }
    }
    return `<svg data-v="${salt}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">${body}</svg>`;
};

// Pool brut de solides candidats. Chacun doit être 3D asymétrique dans les 3
// dimensions (c, r, l) pour avoir 4 vues distinctes. Filtrage runtime ci-dessous.
const POOL_SOLIDES_RAW = [
    // L3D asymétrique : longue branche avant + courte latérale + tour verticale
    { id: 'L3Dasym', cubes: [
        { c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 2, r: 0, l: 0 }, { c: 3, r: 0, l: 0 },
        { c: 0, r: 1, l: 0 }, { c: 0, r: 2, l: 0 },
        { c: 0, r: 0, l: 1 },
    ] },
    // Escalier 3D : marches en profondeur + cube latéral
    { id: 'Escalier3D', cubes: [
        { c: 0, r: 0, l: 0 },
        { c: 1, r: 0, l: 0 }, { c: 1, r: 0, l: 1 },
        { c: 2, r: 0, l: 0 }, { c: 2, r: 0, l: 1 }, { c: 2, r: 0, l: 2 },
        { c: 0, r: 1, l: 0 },
    ] },
    // Chaise 3D : siège + dossier + un accoudoir
    { id: 'Chaise3D', cubes: [
        { c: 0, r: 0, l: 0 }, { c: 0, r: 1, l: 0 }, { c: 0, r: 2, l: 0 },
        { c: 0, r: 0, l: 1 }, { c: 0, r: 0, l: 2 },
        { c: 1, r: 0, l: 0 },
    ] },
    // Coin 3D : 3 cubes aux axes + tour verticale
    { id: 'Coin3D', cubes: [
        { c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 0, r: 1, l: 0 },
        { c: 0, r: 0, l: 1 }, { c: 0, r: 0, l: 2 },
        { c: 1, r: 0, l: 1 },
    ] },
    // Plateau avec tour asymétrique
    { id: 'PlateauTour', cubes: [
        { c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 0, r: 1, l: 0 }, { c: 1, r: 1, l: 0 },
        { c: 2, r: 0, l: 0 },
        { c: 0, r: 0, l: 1 }, { c: 0, r: 0, l: 2 },
    ] },
    // U couché avec 1 cube surélevé
    { id: 'Uhaut', cubes: [
        { c: 0, r: 0, l: 0 }, { c: 1, r: 0, l: 0 }, { c: 2, r: 0, l: 0 }, { c: 3, r: 0, l: 0 },
        { c: 0, r: 1, l: 0 }, { c: 3, r: 1, l: 0 },
        { c: 0, r: 0, l: 1 },
    ] },
];

// Filtrage strict : on ne garde que les solides dont les 4 vues sont DEUX À DEUX
// distinctes (garantit un QCM sans ambiguïté). Calculé une seule fois à l'import.
const POOL_SOLIDES_VUES = POOL_SOLIDES_RAW.filter(s => {
    const axes = ['face', 'dessus', 'gauche', 'droite'];
    const vues = axes.map(ax => viewFromCubes(s.cubes, ax));
    for (let i = 0; i < 4; i++) {
        for (let j = i + 1; j < 4; j++) {
            if (gridEqual(vues[i], vues[j])) return false;
        }
    }
    return true;
});

// ─── Format 1 — Pavé droit (saisie) ──────────────────────────

const genDecomptePave = () => {
    const a = randomChoice(DIMS_POOL);
    const b = randomChoice(DIMS_POOL);
    const c = randomChoice(DIMS_POOL);
    // Un pavé droit a 8 sommets → nom à 8 lettres.
    const letters = pickLetters(8);
    const nom = letters.join('');
    let texte, reponse, suffixe;
    const imageData = svgToDataUrl(buildAssemblageSVG(cubesPave(a, b, c)));

    if (Math.random() < 0.5) {
        // V1 — total à partir des 3 dimensions
        texte = `<p>On construit le pavé droit ${nom} ci-dessous en empilant des petits cubes identiques. Il mesure ${a} cubes de longueur, ${b} cubes de largeur et ${c} cubes de hauteur. Combien contient-il de petits cubes au total ?</p>{{element:0}}`;
        reponse = a * b * c;
        suffixe = ' petits cubes';
    } else {
        // V2 — hauteur manquante
        const N = a * b * c;
        texte = `<p>Le pavé droit ${nom} ci-dessous, composé de petits cubes identiques, contient ${N} petits cubes. Il mesure ${a} cubes de longueur et ${b} cubes de largeur. Quelle est sa hauteur (en nombre de cubes) ?</p>{{element:0}}`;
        reponse = c;
        suffixe = ' cubes';
    }

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_PAVE),
        strict: false,
        suffixe,
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'decompte_pave_saisie';
    return payload;
};

// ─── Format 2 — Grand cube ou complément (saisie) ────────────

const genDecompteCube = () => {
    // Nom de cube paramétré (8 lettres) pour enrichir les fingerprints, même
    // pour V1 qui n'a que 9 valeurs d'arête distinctes.
    const letters = pickLetters(8);
    const nom = letters.join('');
    let texte, reponse, imageData, sousType;

    if (Math.random() < 0.5) {
        // V1 — n³ (cube plein)
        const n = randomChoice(ARETES_POOL);
        imageData = svgToDataUrl(buildAssemblageSVG(cubesPave(n, n, n)));
        texte = `<p>Le grand cube ${nom} ci-dessous est assemblé à partir de petits cubes identiques. Son arête mesure ${n} petits cubes. Combien de petits cubes le composent ?</p>{{element:0}}`;
        reponse = n ** 3;
        sousType = 'cube_plein';
    } else {
        // V2 — complément à m³ (solide partiel visible)
        const m = randomChoice(ARETES_CIBLE_POOL);
        const total = m ** 3;
        const k = randInt(1, total - 1);
        imageData = svgToDataUrl(buildAssemblageSVG(cubesPartiels(m, m, m, k)));
        texte = `<p>Le solide ${nom} ci-dessous est composé de ${k} petits cubes identiques. Combien faudrait-il ajouter de petits cubes pour former un grand cube d'arête ${m} ?</p>{{element:0}}`;
        reponse = total - k;
        sousType = 'complement';
    }

    const payload = makeSaisie(reponse, texte, {
        ...opts(L_CUBE),
        strict: false,
        suffixe: ' petits cubes',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'decompte_cube_saisie';
    // sousType STRUCTUREL (canal contenu, survit au POST) : route le coup de
    // pouce — le complément a sa propre fiche (terminer le cube PUIS soustraire).
    payload.contenu = { ...payload.contenu, sousType };
    return payload;
};

// ─── Format 3 — Vocabulaire du cube (QCM) ────────────────────

const genVocabulaireQcm = () => {
    // Préfixe paramétré par un cube nommé ABCDEFGH (8 lettres distinctes)
    // pour multiplier les fingerprints du format.
    const letters = pickLetters(8);
    const prefix = `Soit un cube ${letters.join('')}.`;

    const r = Math.random();
    let choix, bonne, question;

    if (r < 0.25) {
        // V1 — Faces
        bonne = '6';
        choix = ['4', '6', '8', '12'];
        question = 'Combien un cube possède-t-il de faces ?';
    } else if (r < 0.5) {
        // V2 — Arêtes
        bonne = '12';
        choix = ['8', '10', '12', '24'];
        question = 'Combien un cube possède-t-il d\'arêtes ?';
    } else if (r < 0.75) {
        // V3 — Sommets
        bonne = '8';
        choix = ['6', '8', '10', '12'];
        question = 'Combien un cube possède-t-il de sommets ?';
    } else {
        // V4 — Nature des faces
        bonne = 'Des carrés de même côté.';
        choix = [
            bonne,
            'Des rectangles de dimensions différentes.',
            'Des triangles équilatéraux.',
            'Des parallélogrammes quelconques.',
        ];
        question = 'Un cube est un solide dont toutes les faces sont :';
    }

    const texte = `<p>${prefix} ${question}</p>`;
    const payload = makeQcm(choix, bonne, texte, opts(L_VOCAB));
    payload._meta.generatorKey = 'vocabulaire_cube_qcm';
    return payload;
};

// ─── Format 4 — Propriétés (V/F) ─────────────────────────────

const BUILDERS_V = [
    () => 'Un cube a 6 faces carrées, toutes de même dimension.',
    () => 'Toutes les arêtes d\'un cube ont la même longueur.',
    ({ a, b, c }) => `Un pavé droit composé de ${a}, ${b} et ${c} petits cubes (en longueur, largeur et hauteur) contient ${a * b * c} petits cubes au total.`,
    () => 'Dans un empilement de cubes vu en perspective, certains cubes peuvent être cachés par ceux qui se trouvent devant.',
    ({ n }) => `Un cube d'arête ${n} petits cubes contient ${n ** 3} petits cubes au total.`,
];

const BUILDERS_F = [
    () => 'Un cube a 8 faces.',
    () => 'Pour compter le nombre de petits cubes d\'un empilement, il suffit de compter les cubes visibles sur l\'image.',
    ({ n }) => `Un cube d'arête ${n} petits cubes contient ${n * n} petits cubes au total.`,
    () => 'La vue de face et la vue de dessus d\'un même assemblage sont toujours identiques.',
    () => 'Dans un cube, les faces opposées peuvent avoir des tailles différentes.',
];

const genProprietesVF = () => {
    const texte = '<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>';

    // Retry pattern : si collision de ligne/bonnesReponses, relancer
    for (let attempt = 0; attempt < 5; attempt++) {
        const a = randomChoice(DIMS_POOL);
        const b = randomChoice(DIMS_POOL);
        const c = randomChoice(DIMS_POOL);
        const n = randomChoice(ARETES_POOL);
        const ctx = { a, b, c, n };

        // Tirage de 3V parmi 5
        const idxV = shuffle([0, 1, 2, 3, 4]).slice(0, 3);
        const idxF = shuffle([0, 1, 2, 3, 4]).slice(0, 3);

        const lignesV = idxV.map(i => BUILDERS_V[i](ctx));
        const lignesF = idxF.map(i => BUILDERS_F[i](ctx));

        // 6 lignes mélangées avec leurs réponses (0 = Vrai, 1 = Faux)
        const combined = shuffle([
            ...lignesV.map(l => ({ ligne: l, br: 0 })),
            ...lignesF.map(l => ({ ligne: l, br: 1 })),
        ]);

        const lignes = combined.map(x => x.ligne);
        const bonnesReponses = combined.map(x => x.br);

        // Vérification : pas de doublon de ligne
        if (new Set(lignes).size === lignes.length) {
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'proprietes_cube_vf';
            return payload;
        }
    }

    // Fallback déterministe : V1 V2 V4 + F1 F4 F5 (tous fixes, aucun doublon possible)
    const ligneFallback = [
        'Un cube a 6 faces carrées, toutes de même dimension.',
        'Toutes les arêtes d\'un cube ont la même longueur.',
        'Dans un empilement de cubes vu en perspective, certains cubes peuvent être cachés par ceux qui se trouvent devant.',
        'Un cube a 8 faces.',
        'La vue de face et la vue de dessus d\'un même assemblage sont toujours identiques.',
        'Dans un cube, les faces opposées peuvent avoir des tailles différentes.',
    ];
    const brFallback = [0, 0, 0, 1, 1, 1];
    // Mélange déterministe par indices
    const order = shuffle([0, 1, 2, 3, 4, 5]);
    const lignes = order.map(i => ligneFallback[i]);
    const bonnesReponses = order.map(i => brFallback[i]);
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'proprietes_cube_vf';
    return payload;
};

// ─── Format 6 — Escalier / Empilement (QCM) ──────────────────

const genEscalierQcm = () => {
    const r = Math.random();
    let choix, bonne, texte, imageData, nom, sousType;

    if (r < 0.5) {
        // V1 — Escalier à n marches, largeur p (nom arbitraire, 5 lettres)
        nom = pickLetters(5).join('');
        const n = randomChoice(ESCALIER_POOL);
        const p = randomChoice(LARGEUR_POOL);
        const correct = (p * n * (n + 1)) / 2;
        bonne = String(correct);
        choix = [
            bonne,
            String(p * n * n),   // piège p × n²
            String(n * p),        // piège une seule marche
            String(n + p),        // piège additif
        ];
        // Dédup (au cas où pour de petits p/n les distracteurs coïncideraient)
        choix = [...new Set(choix)];
        while (choix.length < 4) {
            const candidate = String(randInt(1, 200));
            if (!choix.includes(candidate)) choix.push(candidate);
        }
        imageData = svgToDataUrl(buildAssemblageSVG(cubesEscalier(n, p)));
        texte = `<p>L'escalier ${nom} ci-dessous est construit avec des petits cubes identiques. Il comporte ${n} marches. La largeur (profondeur) de chaque marche est de ${p} cubes. Combien de petits cubes composent l'escalier ?</p>{{element:0}}`;
        sousType = 'escalier';
    } else {
        // V2 — Couche inférieure d'un pavé (pavé = 8 sommets)
        nom = pickLetters(8).join('');
        const a = randomChoice(DIMS_POOL);
        const b = randomChoice(DIMS_POOL);
        const c = randomChoice(DIMS_POOL);
        bonne = String(a * b);
        choix = [
            bonne,
            String(a * b * c),
            String(a + b),
            String(a * c),
        ];
        choix = [...new Set(choix)];
        while (choix.length < 4) {
            const candidate = String(randInt(1, 100));
            if (!choix.includes(candidate)) choix.push(candidate);
        }
        imageData = svgToDataUrl(buildAssemblageSVG(cubesPave(a, b, c)));
        texte = `<p>Le pavé droit ${nom} ci-dessous est composé de ${a} × ${b} × ${c} petits cubes identiques (longueur × largeur × hauteur). Combien de ses petits cubes occupent le niveau inférieur (couche de hauteur 1 posée au sol) ?</p>{{element:0}}`;
        sousType = 'couche_sol';
    }

    const payload = makeQcm(choix, bonne, texte, {
        ...opts(L_EMP),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'escalier_empilement_qcm';
    // sousType STRUCTUREL (canal contenu) : V2 n'a AUCUN escalier — sa fiche
    // coup de pouce montre le pavé en couches, pas les marches.
    payload.contenu = { ...payload.contenu, sousType };
    return payload;
};

// ─── Format 7 — Identifier une vue orthogonale (QCM image) ───

const L_VUE_QCM = 'Identifier une vue orthogonale (QCM image)';

const VUES_COLORS = { face: '#facc15', dessus: '#3b82f6', gauche: '#22c55e', droite: '#ef4444' };
const VUES_LABELS = { face: 'de face', dessus: 'de dessus', gauche: 'de gauche', droite: 'de droite' };

const genVueSolideQcm = () => {
    // Le pool est déjà filtré à l'import pour garantir 4 vues distinctes.
    const solide = randomChoice(POOL_SOLIDES_VUES);
    const axes = ['face', 'dessus', 'gauche', 'droite'];
    const vues = {};
    for (const ax of axes) vues[ax] = viewFromCubes(solide.cubes, ax);

    const vueCible = randomChoice(axes);
    const salt = randInt(1, 99999);

    // Construction des 4 choix (chacun = image d'une vue).
    const vueToChoix = {};
    for (const ax of axes) {
        const svg = svgVueGrille(vues[ax], VUES_COLORS[ax], salt + axes.indexOf(ax));
        vueToChoix[ax] = { type: 'image', config: { imageData: svgToDataUrl(svg) } };
    }
    const choix = axes.map(ax => vueToChoix[ax]);
    const correct = vueToChoix[vueCible];

    // Solide 3D dans l'énoncé, avec trièdre d'axes (avant/droite/haut) pour
    // que l'élève sache quelle face est l'avant/la droite/etc.
    const solideImg = svgToDataUrl(buildAssemblageSVGAvecTriedre(solide.cubes));

    const texte = `<p>Observe l'assemblage de cubes ci-dessous. Parmi les 4 figures proposées, laquelle représente la vue <b>${VUES_LABELS[vueCible]}</b> de ce solide ?</p>{{element:0}}`;

    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_VUE_QCM),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData: solideImg } }],
        },
    });
    payload._meta.generatorKey = 'vue_solide_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    decompte_pave_saisie:    genDecomptePave,
    decompte_cube_saisie:    genDecompteCube,
    vocabulaire_cube_qcm:    genVocabulaireQcm,
    proprietes_cube_vf:      genProprietesVF,
    escalier_empilement_qcm: genEscalierQcm,
    vue_solide_qcm:          genVueSolideQcm,
};

export const CATEGORIES = [
    {
        id: 'denombrement_cubes',
        label: 'Dénombrer des assemblages de cubes',
        formatsList: [
            { key: 'decompte_pave_saisie',    label: 'Pavé (saisie)' },
            { key: 'decompte_cube_saisie',    label: 'Cube (saisie)' },
            { key: 'escalier_empilement_qcm', label: 'Escalier (QCM)' },
        ],
    },
    {
        id: 'vocabulaire_cube',
        label: 'Vocabulaire et propriétés du cube',
        formatsList: [
            { key: 'vocabulaire_cube_qcm', label: 'QCM' },
            { key: 'proprietes_cube_vf',   label: 'V/F' },
        ],
    },
    {
        id: 'identifier_vue_solide',
        label: 'Identifier la vue d\'un solide',
        formatsList: [
            { key: 'vue_solide_qcm', label: 'QCM figure' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

// Exposé pour tests unitaires.
export const _internals = {
    LETTRES,
    DIMS_POOL,
    ARETES_POOL,
    ARETES_CIBLE_POOL,
    ESCALIER_POOL,
    LARGEUR_POOL,
    pickLetters,
    viewFromCubes,
    POOL_SOLIDES_RAW,
    POOL_SOLIDES_VUES,
    genDecomptePave,
    genDecompteCube,
    genVocabulaireQcm,
    genProprietesVF,
    genEscalierQcm,
};
